// frontend/src/components/leads/labels/labelsText.js
//
// The lead-labels ("תוויות") feature's ONE copy table, Hebrew and English,
// VERBATIM as the owner approved it (2026-09-29). Every labels surface - the
// ears, the chip row, the labels panel, the create form, the manage dialog,
// the selection bar and the page's toasts - reads its words from here, so a
// wording change is a one-line diff a reviewer can hold against the approval.
//
// In-app noun is "הזדמנות/הזדמנויות", never "ליד". ASCII hyphens only.
// `{n}` / `{name}` are filled by `fillLabels`. A Hebrew sentence with a number
// in it is one template - nothing here is composed from fragments.
//
// `genericError` reuses the app's existing "could not save" sentence
// (ProfessionDescriptionDialog), owner-confirmed for this feature.

const COPY = {
  he: {
    selectPill: "בחירה",
    all: "הכל",
    manage: "ניהול",
    labelsRow: "תוויות",
    back: "חזרה",
    newLabel: "תווית חדשה",
    namePlaceholder: "שם התווית",
    save: "שמירה",
    starterIntro: "אפשר להתחיל מאחת מאלה:",
    // The five defaults (the backend seeds the same five on first load),
    // owner-approved 2026-09-29.
    starterWaiting: "ממתין לתשובה",
    starterMeeting: "נקבעה פגישה",
    starterQuote: "הצעת מחיר נשלחה",
    starterNextMonth: "לחזור בחודש הבא",
    starterDeal: "נסגרה עסקה",
    countMany: "{n} הזדמנויות",
    countOne: "הזדמנות אחת",
    backToAll: "חזרה לכל ההזדמנויות",
    selectedN: "{n} נבחרו",
    selectNone: "סמנו הזדמנויות",
    // The selection bar's select-all checkbox: every selectable opportunity
    // on the current page.
    selectAll: "סמנו הכל",
    labelBtn: "תווית",
    archiveBtn: "ארכיון",
    removeFromLabel: "הסר מהתווית",
    // The selection bar's exit AND the labels panel's footer close.
    closeSelection: "סגור",
    manageTitle: "ניהול תוויות",
    manageHint: "אפשר לשנות שם, צבע וסדר. גררו כדי לסדר.",
    renameAria: "שם התווית",
    colorAria: "צבע התווית",
    dragAria: "גררו לשינוי הסדר",
    deleteAria: "מחיקת התווית",
    limitNote: "הגעת למספר התוויות המרבי. כדי ליצור חדשה, מחקו אחת קיימת.",
    deleteTitle: "למחוק את התווית '{name}'?",
    deleteBodyMany: "{n} ההזדמנויות שבה יישארו בדשבורד.",
    deleteBodyOne: "ההזדמנות שבה תישאר בדשבורד.",
    deleteBodyZero: "אין בה הזדמנויות כרגע.",
    deleteConfirm: "מחיקה",
    close: "סגירה",
    duplicateName: "כבר יש תווית בשם הזה",
    emptyLabelView: "אין הזדמנויות בתווית הזו",
    toastUnavailable: "התווית לא זמינה",
    toastCreated: "התווית '{name}' נוצרה",
    toastAddedN: "התווית נוספה ל-{n} הזדמנויות",
    toastRemovedN: "{n} הזדמנויות הוסרו מהתווית",
    toastAddedOne: "התווית נוספה להזדמנות",
    toastRemovedOne: "ההזדמנות הוסרה מהתווית",
    toastDeleted: "התווית נמחקה",
    genericError: "לא ניתן לשמור כרגע, נסו שוב",
    // The xs filter-bar pill's accessible name (owner-approved 2026-09-29).
    barPillName: "תוויות: {name}",
    // Admin-only: the admin user page's read-only view of a customer's labels
    // (owner-approved admin wording).
    admin: {
      panelTitle: "תוויות הלקוח",
      kindDefault: "ברירת מחדל",
      kindEdited: "ברירת מחדל (נערכה)",
      kindCustom: "תווית של הלקוח",
      deletedDefaults: "ברירות מחדל שנמחקו:",
      notOpened: "הלקוח עוד לא פתח תוויות",
      empty: "אין תוויות",
      loadError: "לא ניתן לטעון את התוויות כרגע",
    },
    colors: {
      turquoise: "טורקיז",
      sky: "תכלת",
      lavender: "לבנדר",
      pink: "ורוד בהיר",
      coral: "אלמוג",
      amber: "ענבר",
      green: "ירוק",
      slate: "אפור",
      indigo: "אינדיגו",
      bordeaux: "בורדו",
    },
  },
  en: {
    selectPill: "Select",
    all: "All",
    manage: "Manage",
    labelsRow: "Labels",
    back: "Back",
    newLabel: "New label",
    namePlaceholder: "Label name",
    save: "Save",
    starterIntro: "You can start with one of these:",
    starterWaiting: "Waiting for a reply",
    starterMeeting: "Meeting scheduled",
    starterQuote: "Quote sent",
    starterNextMonth: "Get back next month",
    starterDeal: "Deal closed",
    countMany: "{n} opportunities",
    countOne: "1 opportunity",
    backToAll: "Back to all opportunities",
    selectedN: "{n} selected",
    selectNone: "Mark opportunities",
    selectAll: "Select all",
    labelBtn: "Label",
    archiveBtn: "Archive",
    removeFromLabel: "Remove from label",
    closeSelection: "Close",
    manageTitle: "Manage labels",
    manageHint: "Rename, recolor and reorder. Drag to reorder.",
    renameAria: "Label name",
    colorAria: "Label colour",
    dragAria: "Drag to reorder",
    deleteAria: "Delete label",
    limitNote:
      "You reached the maximum number of labels. Delete one to create a new one.",
    deleteTitle: "Delete the label '{name}'?",
    deleteBodyMany: "The {n} opportunities in it will stay on the dashboard.",
    deleteBodyOne: "The one opportunity in it will stay on the dashboard.",
    deleteBodyZero: "It has no opportunities right now.",
    deleteConfirm: "Delete",
    close: "Close",
    duplicateName: "A label with this name already exists",
    emptyLabelView: "No opportunities in this label",
    toastUnavailable: "The label is not available",
    toastCreated: "Label '{name}' created",
    toastAddedN: "Label added to {n} opportunities",
    toastRemovedN: "{n} opportunities removed from the label",
    toastAddedOne: "Label added to the opportunity",
    toastRemovedOne: "The opportunity was removed from the label",
    toastDeleted: "Label deleted",
    genericError: "Could not save right now, please try again",
    // The xs filter-bar pill's accessible name (owner-approved 2026-09-29).
    barPillName: "Labels: {name}",
    admin: {
      panelTitle: "Customer labels",
      kindDefault: "Default",
      kindEdited: "Default (edited)",
      kindCustom: "Custom",
      deletedDefaults: "Deleted defaults:",
      notOpened: "The customer has not opened labels yet",
      empty: "No labels",
      loadError: "Could not load labels right now",
    },
    colors: {
      turquoise: "Turquoise",
      sky: "Sky blue",
      lavender: "Lavender",
      pink: "Light pink",
      coral: "Coral",
      amber: "Amber",
      green: "Green",
      slate: "Slate grey",
      indigo: "Indigo",
      bordeaux: "Bordeaux",
    },
  },
};

/**
 * @param {boolean} isHebrew
 * @returns {typeof COPY.he} the language's table.
 */
export const labelsText = (isHebrew) => (isHebrew ? COPY.he : COPY.en);

/**
 * Substitute `{name}` placeholders. A missing value is left as its own
 * placeholder rather than printed as "undefined", so a wiring mistake shows
 * up in review instead of shipping as a broken sentence.
 *
 * @param {string} template
 * @param {Record<string, string|number>} [vars]
 * @returns {string}
 */
export function fillLabels(template, vars = {}) {
  return String(template).replace(/\{(\w+)\}/g, (whole, key) =>
    vars[key] === undefined || vars[key] === null ? whole : String(vars[key])
  );
}

/** "{n} הזדמנויות" / "הזדמנות אחת" - the singular is its own sentence. */
export function countText(n, isHebrew) {
  const t = labelsText(isHebrew);
  return Number(n) === 1 ? t.countOne : fillLabels(t.countMany, { n });
}

/** The selection bar's count line: "{n} נבחרו", or the prompt at zero. */
export function selectedText(n, isHebrew) {
  const t = labelsText(isHebrew);
  const count = Number(n) || 0;
  return count > 0 ? fillLabels(t.selectedN, { n: count }) : t.selectNone;
}

/** "Label added to {n}" - the approved singular sentence for exactly one. */
export function addedText(n, isHebrew) {
  const t = labelsText(isHebrew);
  return Number(n) === 1 ? t.toastAddedOne : fillLabels(t.toastAddedN, { n });
}

/** "{n} removed from the label" - the approved singular sentence for exactly one. */
export function removedText(n, isHebrew) {
  const t = labelsText(isHebrew);
  return Number(n) === 1 ? t.toastRemovedOne : fillLabels(t.toastRemovedN, { n });
}

/** The delete confirm's body for a label holding `n` opportunities. */
export function deleteBodyText(n, isHebrew) {
  const t = labelsText(isHebrew);
  const count = Number(n) || 0;
  if (count <= 0) return t.deleteBodyZero;
  if (count === 1) return t.deleteBodyOne;
  return fillLabels(t.deleteBodyMany, { n: count });
}

/** A colour key's accessible name ("טורקיז" / "Turquoise"). */
export function colorName(key, isHebrew) {
  const names = labelsText(isHebrew).colors;
  return Object.prototype.hasOwnProperty.call(names, key) ? names[key] : names.slate;
}

export default COPY;
